import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlacements } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';

import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import FeaturedOffer502x272 from './components/FeaturedOffer502x272/FeaturedOffer502x272';
import List from 'core/modules/Partials/List/List';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';

import { featuredOffers502x272IsViewed } from './actions';

import {
  selectFeaturedOffers502x272Config, makeSelectFeaturedOffers502x272Data,
  makeSelectFeaturedOffers502x272IsLoaded, makeSelectFeaturedOffers502x272IsLoading,
} from './selectors';

import './FeaturedOffers502x272.scss';

function FeaturedOffers502x272({
  namespace = 'default', content_group_id, title, pill, view_type: viewType,
}) {
  const dispatch = useDispatch();
  const selectFeaturedOffers502x272Data = useMemo(makeSelectFeaturedOffers502x272Data, []);
  const selectFeaturedOffers502x272IsLoaded = useMemo(makeSelectFeaturedOffers502x272IsLoaded, []);
  const selectFeaturedOffers502x272IsLoading = useMemo(makeSelectFeaturedOffers502x272IsLoading, []);

  const data = useSelector(state => selectFeaturedOffers502x272Data(state, namespace));
  const config = useSelector(selectFeaturedOffers502x272Config);
  const isLoaded = useSelector(state => selectFeaturedOffers502x272IsLoaded(state, namespace));
  const isLoading = useSelector(state => selectFeaturedOffers502x272IsLoading(state, namespace));
  const isMobileMQ = useSelector(selectIsMobileMQ);

  const { api: { params, options }, useCarousel } = config;

  const callFetchPlacements = () => {
    const paramsWithContentGroup = {
      content_group_id,
      ...params,
    };

    if (!paramsWithContentGroup.content_group_id && !paramsWithContentGroup.offer_tag) {
      return false; // content_group_id or offer_tag param is required for /placements API endpoint
    }

    dispatch(fetchPlacements(paramsWithContentGroup, { ...options, namespace }));
    return true;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      callFetchPlacements();
    }
  });

  useEffect(() => {
    if (isLoaded && data.length) {
      data.forEach((deal) => {
        dispatch(featuredOffers502x272IsViewed(deal, data.length));
      });
    }
  }, [data, dispatch, isLoaded]);

  if (isEmpty(data)) {
    return null;
  }

  const shouldUseCarousel =
    isMobileMQ ? data.length > 1 && useCarousel : viewType?.includes('carousel') && data.length > 1 && useCarousel;
  const viewTypeName = viewType === '3_columns' ? 'Featured 328x178' : 'Featured 502x272';
  const sectionItemsClassName = shouldUseCarousel ? 'mn_carousel' : 'mn_grid';

  return (
    <div
      className="mn_featuredOffers502x272"
      data-test="featured-offers-502x272"
      data-title={title}
      data-view-name={viewTypeName}
    >
      <SectionTitle title={title} pill={pill} />
      <div className={`mn_featuredOffers502x272Items ${sectionItemsClassName}`}>
        <List options={config.carousel} shouldUseCarousel={shouldUseCarousel}>
          {data.map(dataItem => (
            <FeaturedOffer502x272 key={dataItem.id} dataItem={dataItem} shouldUseCarousel={shouldUseCarousel} />
          ))}
        </List>
      </div>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><FeaturedOffers502x272 {...props} /></ComponentErrorBoundary>;
